/* "Missed today" -- compact Home dashboard card.
 *
 * Shows a bucketized summary of unread notifications the user missed in
 * the last 24 hours. Auto-hides in three cases:
 *   1. GET fails (no reason to noise the Home page)
 *   2. suppressed=true (user opted-in for the daily email digest)
 *   3. count=0 (nothing to show)
 *
 * Consistency Sprint: buckets are click-to-toggle. When a bucket is
 * expanded we render the top-3 preview rows (already provided by the
 * `/notifications/missed-today` payload) inline, each linking to its
 * native surface. First bucket auto-opens so there is always at least
 * one row visible.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, ChevronDown, MailCheck } from "lucide-react";
import api from "@/lib/api";

export default function MissedTodayCard() {
  const [data, setData] = useState(null);
  const [openBucket, setOpenBucket] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/notifications/missed-today");
        if (!cancelled) {
          setData(data);
          // Auto-open the first bucket so at least one preview row shows.
          if (data?.buckets?.length) setOpenBucket(data.buckets[0].label);
        }
      } catch { if (!cancelled) setData({ suppressed: true }); }
    })();
    return () => { cancelled = true; };
  }, []);

  const buckets = useMemo(() => data?.buckets || [], [data]);

  if (!data || data.suppressed || (data.count || 0) === 0) return null;

  const bucketCount = buckets.length;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        className="mb-4 rounded-2xl border border-sky-500/25 bg-gradient-to-r from-brand-blue/20 via-sky-500/[0.06] to-transparent backdrop-blur-md p-4"
        data-testid="missed-today-card"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-sky-500/20 border border-sky-500/40 flex items-center justify-center shrink-0">
            <MailCheck className="h-4 w-4 text-sky-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sky-300">Missed in the last 24 hours</div>
            <div className="text-sm text-white mt-0.5">
              <span className="font-semibold">{data.count} update{data.count !== 1 ? "s" : ""}</span>
              <span className="text-foreground/60"> across {bucketCount} categor{bucketCount !== 1 ? "ies" : "y"}</span>
            </div>

            <div className="mt-3 space-y-2">
              {buckets.map((b) => {
                const isOpen = openBucket === b.label;
                const testKey = b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                return (
                  <div key={b.label} className="rounded-xl border border-white/[0.06] bg-white/[0.03]" data-testid={`missed-bucket-${testKey}`}>
                    <button
                      type="button"
                      onClick={() => setOpenBucket(isOpen ? null : b.label)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left"
                      data-testid={`missed-bucket-toggle-${testKey}`}
                    >
                      <Bell className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                      <span className="text-[12px] text-white/90 font-medium">{b.label}</span>
                      <span className="bg-sky-500/20 text-sky-300 rounded-full px-2 py-[1px] text-[10px]">{b.count}</span>
                      <Link
                        to={b.jump_url}
                        onClick={(e) => e.stopPropagation()}
                        className="ml-auto text-[11px] text-sky-300 hover:text-sky-200 transition"
                        data-testid={`missed-bucket-jump-${testKey}`}
                      >
                        View all
                      </Link>
                      <ChevronDown className={"h-3.5 w-3.5 text-white/50 transition-transform " + (isOpen ? "rotate-180" : "")} />
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (b.preview || []).length > 0 && (
                        <motion.ul
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="border-t border-white/[0.04] divide-y divide-white/[0.03] overflow-hidden"
                          data-testid={`missed-bucket-preview-${testKey}`}
                        >
                          {(b.preview || []).map((row) => (
                            <li key={row.id}>
                              <Link
                                to={row.link || b.jump_url}
                                className="block px-4 py-2 hover:bg-white/[0.03] transition"
                                data-testid={`missed-preview-row-${row.id}`}
                              >
                                <div className="text-[12px] text-white truncate">{row.title || "Untitled"}</div>
                                {row.body && (
                                  <div className="text-[11px] text-foreground/55 truncate mt-0.5">{row.body}</div>
                                )}
                              </Link>
                            </li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
